import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { sbGetSetting, sbSetSetting } from "../utils/supabaseClient";
import { sendNotification } from "../utils/notifications";
import { useToast } from "./Toast";
import PullToRefresh from "./PullToRefresh";
import { SVG_ICONS } from "./TablerIconPicker";
import "../styles/ListTab.css";

const ResourceListTab = forwardRef(({ onHeaderInfoChange }, ref) => {
  const { success, error } = useToast();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [existingCategories, setExistingCategories] = useState([]);
  const [editingCategoryMode, setEditingCategoryMode] = useState("select");

  useEffect(() => {
    loadResources();
  }, []);

  useImperativeHandle(ref, () => ({
    loadResources
  }));

  useEffect(() => {
    onHeaderInfoChange?.({ title: "Resources", count: resources.length });
  }, [resources, onHeaderInfoChange]);

  const loadResources = async () => {
    try {
      setLoading(true);
      const data = await sbGetSetting("origamic-resources");
      const sorted = Array.isArray(data)
        ? [...data].sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt))
        : [];
      setResources(sorted);
    } catch (err) {
      console.error("Error loading resources:", err);
      error("Failed to load resources");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this resource?"))
      return;

    try {
      const updated = resources.filter((r) => r.id !== id);
      await sbSetSetting("origamic-resources", updated);
      setResources(updated);
      success("Resource deleted");
    } catch (err) {
      console.error("Error deleting resource:", err);
      error("Failed to delete resource");
    }
  };

  const handleEdit = async (resource) => {
    setEditingId(resource.id);
    setEditFormData({ ...resource });
    setEditingCategoryMode("select");

    try {
      const allResources = await sbGetSetting("origamic-resources");
      if (Array.isArray(allResources)) {
        const categories = [...new Set(allResources.map(r => r.category).filter(Boolean))].sort();
        setExistingCategories(categories);
      }
    } catch (err) {
      console.error("Error loading categories:", err);
    }
  };

  const handleSaveEdit = async () => {
    try {
      const oldResource = resources.find((r) => r.id === editingId);
      const wasPublished = oldResource?.draft && !editFormData.draft;

      const updated = resources.map((r) =>
        r.id === editingId ? editFormData : r
      );
      await sbSetSetting("origamic-resources", updated);

      if (wasPublished) {
        await sendNotification(
          `🔗 New Resource • ${editFormData.title}`,
          editFormData.description || editFormData.url,
          "resources",
          editFormData.id
        );
      }

      setResources(updated);
      setEditingId(null);
      success("Resource updated");
    } catch (err) {
      console.error("Error saving resource:", err);
      error("Failed to save resource");
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditFormData({});
    setEditingCategoryMode("select");
  };

  const handleToggleDraft = async (resource) => {
    try {
      const newDraftStatus = !resource.draft;

      const updated = resources.map(r =>
        r.id === resource.id ? { ...r, draft: newDraftStatus } : r
      );
      await sbSetSetting("origamic-resources", updated);

      if (newDraftStatus === false) {
        // Publishing: send notification
        await sendNotification(
          `🔗 New Resource • ${resource.title}`,
          resource.description || resource.url,
          "resources",
          resource.id
        );
      }

      setResources(updated);
    } catch (err) {
      console.error("Error toggling draft status:", err);
      error("Failed to update draft status");
    }
  };

  if (loading) return <div className="loading">Loading resources...</div>;

  if (resources.length === 0)
    return (
      <div className="empty-state">No resources found. Create one first!</div>
    );

  // Get unique categories from resources
  const uniqueCategories = [...new Set(resources.map(resource => resource.category).filter(Boolean))].sort();

  const filteredResources = resources.filter((resource) => {
    // Filter by search query
    const matchesSearch =
      resource.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.description?.toLowerCase().includes(searchQuery.toLowerCase());

    // Filter by selected category
    const matchesCategory = selectedCategory === null || resource.category === selectedCategory;

    // Filter by selected status (Published vs Draft)
    const matchesStatus = selectedStatus === null || (
      (selectedStatus === "published" && !resource.draft) ||
      (selectedStatus === "draft" && resource.draft)
    );

    return matchesSearch && matchesCategory && matchesStatus;
  });

  return (
    <div className="list-container">
      <input
        type="text"
        placeholder="Search resources..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={{
          width: "100%",
          padding: "10px 12px",
          marginBottom: "12px",
          background: "#050e1a",
          border: "1px solid rgba(59, 130, 246, 0.2)",
          borderRadius: "6px",
          color: "#fff",
          fontSize: "14px",
          boxSizing: "border-box",
        }}
      />

      {/* Category Filter Pills */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "20px", flexWrap: "wrap" }}>
        <button
          onClick={() => setSelectedCategory(null)}
          style={{
            padding: "6px 14px",
            background: selectedCategory === null ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.05)",
            color: selectedCategory === null ? "#5B9EF0" : "#7A9CC4",
            border: selectedCategory === null ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
            borderRadius: "20px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: "500",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            if (selectedCategory !== null) {
              e.target.style.background = "rgba(59, 130, 246, 0.1)";
              e.target.style.borderColor = "rgba(59, 130, 246, 0.3)";
            }
          }}
          onMouseLeave={(e) => {
            if (selectedCategory !== null) {
              e.target.style.background = "rgba(59, 130, 246, 0.05)";
              e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
            }
          }}
        >
          All
        </button>
        {uniqueCategories.map((category) => {
          const count = resources.filter(resource => resource.category === category).length;
          return (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              style={{
                padding: "6px 14px",
                background: selectedCategory === category ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.05)",
                color: selectedCategory === category ? "#5B9EF0" : "#7A9CC4",
                border: selectedCategory === category ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
                borderRadius: "20px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "500",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                if (selectedCategory !== category) {
                  e.target.style.background = "rgba(59, 130, 246, 0.1)";
                  e.target.style.borderColor = "rgba(59, 130, 246, 0.3)";
                }
              }}
              onMouseLeave={(e) => {
                if (selectedCategory !== category) {
                  e.target.style.background = "rgba(59, 130, 246, 0.05)";
                  e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
                }
              }}
            >
              {category} ({count})
            </button>
          );
        })}
      </div>

      {/* Status Filter Pills */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "20px", flexWrap: "wrap" }}>
        <button
          onClick={() => setSelectedStatus(null)}
          style={{
            padding: "6px 14px",
            background: selectedStatus === null ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.05)",
            color: selectedStatus === null ? "#5B9EF0" : "#7A9CC4",
            border: selectedStatus === null ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
            borderRadius: "20px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: "500",
            transition: "all 0.2s",
          }}
        >
          All
        </button>
        {["published", "draft"].map((status) => {
          const label = status.charAt(0).toUpperCase() + status.slice(1);
          return (
            <button
              key={status}
              onClick={() => setSelectedStatus(status)}
              style={{
                padding: "6px 14px",
                background: selectedStatus === status ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.05)",
                color: selectedStatus === status ? "#5B9EF0" : "#7A9CC4",
                border: selectedStatus === status ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
                borderRadius: "20px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "500",
                transition: "all 0.2s",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {editingId ? (
        // Edit Form
        <div className="edit-form">
          <h3>Edit Resource</h3>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={editFormData.title || ""}
              onChange={(e) =>
                setEditFormData({ ...editFormData, title: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Category</label>
            {editingCategoryMode === "select" ? (
              <select
                value={editFormData.category || ""}
                onChange={(e) => {
                  if (e.target.value === "new") {
                    setEditingCategoryMode("add");
                  } else {
                    setEditFormData({ ...editFormData, category: e.target.value });
                  }
                }}
              >
                <option value="">-- Select Category --</option>
                {existingCategories.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
                <option value="new">+ Add New Category</option>
              </select>
            ) : (
              <>
                <input
                  type="text"
                  value={editFormData.category || ""}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, category: e.target.value })
                  }
                  placeholder="Enter new category name..."
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => {
                    setEditingCategoryMode("select");
                  }}
                  style={{
                    marginTop: "8px",
                    padding: "8px 12px",
                    background: "#7A9CC4",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "12px",
                  }}
                >
                  Back to Categories
                </button>
              </>
            )}
          </div>
          <div className="form-group">
            <label>URL</label>
            <input
              type="url"
              value={editFormData.url || ""}
              onChange={(e) =>
                setEditFormData({ ...editFormData, url: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={editFormData.description || ""}
              onChange={(e) =>
                setEditFormData({
                  ...editFormData,
                  description: e.target.value,
                })
              }
              rows="4"
            />
          </div>
          <div className="form-group">
            <label>Scheduled Date (Optional)</label>
            <input
              type="date"
              value={editFormData.scheduled ? new Date(editFormData.scheduled).toISOString().slice(0, 10) : ""}
              onChange={(e) =>
                setEditFormData({ ...editFormData, scheduled: e.target.value ? new Date(e.target.value).toISOString() : null })
              }
              title="Schedule when this resource should be published"
              style={{ width: "100%" }}
            />
            <small style={{ color: "#7A9CC4", marginTop: "4px", display: "block" }}>
              Optional: Schedule this resource to be published on a future date
            </small>
          </div>
          <div className="form-group">
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={!editFormData.draft}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, draft: !e.target.checked })
                }
              />
              Published (unpublished resources stay in draft and don't notify users)
            </label>
          </div>
          <div className="form-actions">
            <button className="btn-save" onClick={handleSaveEdit}>
              Save Changes
            </button>
            <button className="btn-cancel" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        // Resources List
        <PullToRefresh onRefresh={loadResources}>
          {filteredResources.length === 0 && searchQuery && (
            <div className="empty-state">No resources match your search</div>
          )}
          {filteredResources.length > 0 && (
          <div className="items-grid">
            {filteredResources.map((resource) => (
              <div key={resource.id} className="item-card">
                <div className="item-content">
                  <h4>{resource.title}</h4>
                  {resource.category && (
                    <p className="item-meta">Category: {resource.category}</p>
                  )}
                  <a
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="item-link"
                    style={{ display: "flex", alignItems: "center", gap: "4px" }}
                  >
                    {SVG_ICONS["link"]({ size: 14, color: "#3B82F6" })}
                    {resource.url}
                  </a>
                  {resource.description && (
                    <p className="item-preview">{resource.description}</p>
                  )}
                  <div style={{display: "flex", gap: "8px", marginTop: "8px"}}>
                    {resource.draft ? (
                      <div style={{display: "flex", alignItems: "center", gap: "4px", padding: "4px 12px", background: "rgba(245, 158, 11, 0.2)", color: "#F59E0B", borderRadius: "6px", fontSize: "12px", fontWeight: "600"}}>
                        {SVG_ICONS["alert-circle"]({ size: 14, color: "#F59E0B" })}
                        DRAFT
                      </div>
                    ) : (
                      <div style={{display: "flex", alignItems: "center", gap: "4px", padding: "4px 12px", background: "#22C55E20", color: "#22C55E", borderRadius: "6px", fontSize: "12px", fontWeight: "600"}}>
                        {SVG_ICONS["check-circle"]({ size: 14, color: "#22C55E" })}
                        PUBLISHED
                      </div>
                    )}
                  </div>
                </div>
                <div className="item-actions">
                  <button
                    onClick={() => handleToggleDraft(resource)}
                    title={resource.draft ? "Publish resource" : "Revert to draft"}
                    style={{
                      padding: "6px 12px",
                      background: resource.draft ? "rgba(34, 197, 94, 0.1)" : "rgba(245, 158, 11, 0.1)",
                      color: resource.draft ? "#22C55E" : "#F59E0B",
                      border: resource.draft ? "1px solid rgba(34, 197, 94, 0.2)" : "1px solid rgba(245, 158, 11, 0.2)",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "4px",
                    }}
                    onMouseEnter={(e) => {
                      if (resource.draft) {
                        e.target.style.background = "rgba(34, 197, 94, 0.2)";
                        e.target.style.borderColor = "rgba(34, 197, 94, 0.4)";
                      } else {
                        e.target.style.background = "rgba(245, 158, 11, 0.2)";
                        e.target.style.borderColor = "rgba(245, 158, 11, 0.4)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (resource.draft) {
                        e.target.style.background = "rgba(34, 197, 94, 0.1)";
                        e.target.style.borderColor = "rgba(34, 197, 94, 0.2)";
                      } else {
                        e.target.style.background = "rgba(245, 158, 11, 0.1)";
                        e.target.style.borderColor = "rgba(245, 158, 11, 0.2)";
                      }
                    }}
                  >
                    {resource.draft
                      ? SVG_ICONS["check"]({ size: 18, color: "#22C55E" })
                      : SVG_ICONS["circle-x"]({ size: 18, color: "#F59E0B" })}
                    <span style={{ fontSize: "12px" }}>
                      {resource.draft ? "Publish" : "Draft"}
                    </span>
                  </button>
                  <button
                    onClick={() => handleEdit(resource)}
                    style={{
                      padding: "6px 12px",
                      background: "rgba(59, 130, 246, 0.1)",
                      color: "#5B9EF0",
                      border: "1px solid rgba(59, 130, 246, 0.2)",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "rgba(59, 130, 246, 0.2)";
                      e.target.style.borderColor = "rgba(59, 130, 246, 0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "rgba(59, 130, 246, 0.1)";
                      e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
                    }}
                  >
                    {SVG_ICONS["pencil"]({ size: 18, color: "#5B9EF0" })}
                  </button>
                  <button
                    onClick={() => handleDelete(resource.id)}
                    style={{
                      padding: "6px 12px",
                      background: "rgba(59, 130, 246, 0.1)",
                      color: "#5B9EF0",
                      border: "1px solid rgba(59, 130, 246, 0.2)",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "rgba(59, 130, 246, 0.2)";
                      e.target.style.borderColor = "rgba(59, 130, 246, 0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "rgba(59, 130, 246, 0.1)";
                      e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
                    }}
                  >
                    {SVG_ICONS["trash"]({ size: 18, color: "#5B9EF0" })}
                  </button>
                </div>
              </div>
            ))}
          </div>
          )}
        </PullToRefresh>
      )}
    </div>
  );
});

ResourceListTab.displayName = 'ResourceListTab';

export default ResourceListTab;
